import React, { useMemo, useState } from "react";
import { computeAnalystRecord } from "../lib/analystRecord";

const ROWS = 6;
const CELL = 22; // px
const GAP = 2;

/**
 * Baccarat-style "Big Road" visualization for a team's W/L sequence.
 *  - Red dot = Win (W)
 *  - Blue dot = Loss (L)
 *  - Green diagonal line on previous cell = Tie (rare in MLB but supported)
 *  - New column starts on each streak flip (W↔L)
 *  - Within a column, results descend (up to 6 rows). If streak > 6, "dragon tail":
 *    columns shift right but stay at row 6.
 *
 * Props:
 *  - results: array of 'W' | 'L' | 'T' (most recent last)
 *  - title: team display name
 *  - subtitle: optional small label (e.g. last-N record summary)
 */
export default function BigRoad({ results = [], title, subtitle, mode = "actual", analystFilter = "both" }) {
  const { grid, cols } = useMemo(() => buildGrid(results), [results]);
  const _res = (r) => (typeof r === "string" ? r : r?.result);
  const wins = results.filter((r) => _res(r) === "W").length;
  const losses = results.filter((r) => _res(r) === "L").length;
  const { actualW, actualL, theoW, theoL, skippedW, skippedL, skipped: skippedTotal } =
    useMemo(() => computeAnalystRecord(results, analystFilter, mode), [results, analystFilter, mode]);
  const showActual = mode === "actual" || mode === "diff";
  const showTheo = mode === "theoretical" || mode === "diff";
  const filterTag = analystFilter === "charlie" ? "C" : analystFilter === "dakota" ? "D" : null;
  const analystLabel = analystFilter === "charlie" ? "Charlie" : analystFilter === "dakota" ? "Dakota" : "any analyst";

  return (
    <div className="bg-[#121212] border border-white/10 rounded-sm p-4" data-testid={`big-road-${title}`}>
      <div className="flex items-baseline justify-between mb-2 gap-3">
        <div className="min-w-0">
          <div className="font-display text-lg tracking-wider truncate">{title}</div>
          {subtitle && <div className="text-[10px] uppercase tracking-widest text-zinc-500">{subtitle}</div>}
        </div>
        <div className="flex gap-2 text-[10px] font-mono-d items-center whitespace-nowrap">
          <span className="text-red-500">{wins}W</span>
          <span className="text-blue-400">{losses}L</span>
          {showActual && (actualW + actualL) > 0 && (
            <span className="ml-1 px-1.5 py-0.5 border border-white bg-zinc-800 text-white rounded-sm" title={`Actual picks placed by ${analystLabel}`}>
              ● {filterTag && <span className="text-zinc-400 mr-0.5">{filterTag}</span>}{actualW}-{actualL}
            </span>
          )}
          {showTheo && (theoW + theoL) > 0 && (
            <span
              className="px-1.5 py-0.5 border border-dashed border-white/60 bg-zinc-800 text-zinc-200 rounded-sm"
              title={`${analystLabel} qualified bets · ${theoW}W · ${theoL}L · ${skippedTotal} skipped (${skippedW}W would-have-won · ${skippedL}L would-have-lost)`}
            >
              ◌ {filterTag && <span className="text-zinc-400 mr-0.5">{filterTag}</span>}{theoW}-{theoL}-{skippedTotal}
              {skippedTotal > 0 && (
                <span className="ml-1 text-zinc-500">
                  · skip <span className="text-red-400">{skippedW}W</span>·<span className="text-blue-400">{skippedL}L</span>
                </span>
              )}
            </span>
          )}
        </div>
      </div>
      <div className="overflow-x-auto" style={{ scrollbarWidth: "thin" }}>
        <div
          className="grid bg-black/40 p-1 rounded-sm"
          style={{
            gridTemplateColumns: `repeat(${Math.max(cols, 24)}, ${CELL}px)`,
            gridTemplateRows: `repeat(${ROWS}, ${CELL}px)`,
            gap: `${GAP}px`,
            minWidth: "100%",
          }}
        >
          {Array.from({ length: ROWS * Math.max(cols, 24) }, (_, idx) => {
            const r = Math.floor(idx / Math.max(cols, 24));
            const c = idx % Math.max(cols, 24);
            const cell = grid[c]?.[r];
            return (
              <Cell key={idx} cell={cell} mode={mode} analystFilter={analystFilter} />
            );
          })}
        </div>
      </div>
    </div>
  );
}

function Cell({ cell, mode = "actual", analystFilter = "both" }) {
  // analystFilter: "both" | "charlie" | "dakota"
  const [popoverOpen, setPopoverOpen] = useState(false);
  if (!cell) {
    return <div className="bg-zinc-900/40" style={{ width: CELL, height: CELL }} />;
  }
  const isWin = cell.result === "W";
  // Apply analyst filter — keep only the analyst-of-interest in each layer.
  const filterArr = (arr) => {
    if (analystFilter === "both") return arr || [];
    const keep = analystFilter === "charlie" ? "Charlie" : "Dakota";
    return (arr || []).filter((a) => a === keep);
  };
  const actualAnalysts = filterArr(cell.actual_analysts || (mode !== "theoretical" ? cell.pick_analysts : null));
  const theoAnalysts = filterArr(cell.theoretical_analysts || (mode === "theoretical" ? cell.pick_analysts : null));
  const skipAnalysts = filterArr(cell.skipped_analysts);
  const skipReasons = (cell.skip_reasons || []).filter((sr) => {
    if (analystFilter === "both") return true;
    return sr.analyst === (analystFilter === "charlie" ? "Charlie" : "Dakota");
  });

  let ringStyle = "none";
  if (mode === "actual") {
    ringStyle = actualAnalysts.length > 0 ? "solid" : "none";
  } else if (mode === "theoretical") {
    if (theoAnalysts.length > 0) ringStyle = "solid";
    else if (skipAnalysts.length > 0) ringStyle = "skip";
  } else {
    if (actualAnalysts.length > 0) ringStyle = "solid";
    else if (theoAnalysts.length > 0) ringStyle = "dashed";
    else if (skipAnalysts.length > 0) ringStyle = "skip";
  }
  const labelSrc = (() => {
    if (mode === "actual") return actualAnalysts;
    if (mode === "theoretical") return theoAnalysts.length ? theoAnalysts : skipAnalysts;
    if (actualAnalysts.length) return actualAnalysts;
    if (theoAnalysts.length) return theoAnalysts;
    return skipAnalysts;
  })();
  const resultLetter = isWin ? "W" : "L";
  const analystLetter = labelSrc.length === 0
    ? ""
    : labelSrc.length > 1 ? "B" : labelSrc[0].toLowerCase().startsWith("c") ? "C" : "D";
  const label = ringStyle !== "none" ? `${resultLetter}${analystLetter}` : null;
  const titleParts = [cell.result];
  if (cell.date) titleParts.push(cell.date);
  if (actualAnalysts.length) titleParts.push(`Actual: ${actualAnalysts.join(" + ")}`);
  if (theoAnalysts.length) titleParts.push(`Theoretical: ${theoAnalysts.join(" + ")}`);
  if (skipAnalysts.length) titleParts.push(`Skipped: ${skipAnalysts.join(" + ")} — click for reason`);
  const ringClass = ringStyle === "solid" ? "ring-2 ring-white ring-offset-0" : "";
  const ringInline =
    ringStyle === "dashed" ? { outline: "2px dashed #ffffff", outlineOffset: 0 } :
    ringStyle === "skip" ? { outline: "2px solid #9ca3af", outlineOffset: 0, opacity: 0.5 } :
    {};
  const isClickable = ringStyle === "skip" && skipReasons.length > 0;
  return (
    <div
      className="relative flex items-center justify-center"
      style={{ width: CELL, height: CELL }}
      title={titleParts.join(" · ")}
    >
      <span
        onClick={isClickable ? (e) => { e.stopPropagation(); setPopoverOpen((o) => !o); } : undefined}
        className={`block rounded-full flex items-center justify-center ${isWin ? "bg-red-600" : "bg-blue-500"} ${ringClass} ${isClickable ? "cursor-pointer hover:opacity-100" : ""}`}
        style={{
          width: CELL - 6,
          height: CELL - 6,
          boxShadow: ringStyle === "solid" || ringStyle === "dashed" ? "inset 0 0 0 1px rgba(0,0,0,0.35)" : "none",
          ...ringInline,
        }}
      >
        {label && (
          <span
            className="font-bold leading-none text-white select-none"
            style={{ fontSize: 8, letterSpacing: "-0.5px" }}
          >{label}</span>
        )}
      </span>
      {cell.tieAfter && (
        <span
          className="absolute"
          style={{
            width: CELL,
            height: 2,
            background: "linear-gradient(45deg, transparent 0%, #22C55E 25%, #22C55E 75%, transparent 100%)",
            transform: "rotate(-45deg)",
          }}
        />
      )}
      {popoverOpen && skipReasons.length > 0 && (
        <div
          onClick={(e) => e.stopPropagation()}
          className="absolute z-50 bg-[#1a1a1a] border border-white/25 rounded-sm shadow-2xl p-3 text-[10px] text-zinc-200"
          style={{ left: "100%", marginLeft: 6, top: 0, minWidth: 220 }}
          role="dialog"
          data-testid={`skip-reason-popover-${cell.date}`}
        >
          <div className="text-[9px] uppercase tracking-widest text-zinc-500 mb-1.5">Why skipped · {cell.date}</div>
          {skipReasons.map((sr, i) => (
            <div key={i} className="mb-1 last:mb-0 leading-tight">
              <span className={`font-bold mr-1 ${sr.analyst === "Charlie" ? "text-[#5BA8FF]" : "text-[#D8B4FE]"}`}>{sr.analyst}:</span>
              <span className="text-zinc-300">{sr.reason}</span>
            </div>
          ))}
          <button
            onClick={() => setPopoverOpen(false)}
            className="mt-2 w-full text-[9px] uppercase tracking-widest text-zinc-500 hover:text-white"
          >Close</button>
        </div>
      )}
    </div>
  );
}

/**
 * Build a baccarat-style grid (columns of W/L runs with dragon-tail spillover).
 * grid is a 2D array indexed [col][row]. Each cell = { result: 'W'|'L', date, tieAfter }
 */
function buildGrid(results) {
  const grid = [];
  if (!results.length) return { grid, cols: 0 };

  let col = 0; let row = 0; let lastResult = null;

  const placeAt = (c, r, cell) => {
    if (!grid[c]) grid[c] = [];
    grid[c][r] = cell;
  };

  for (let i = 0; i < results.length; i++) {
    const item = typeof results[i] === "string" ? { result: results[i] } : results[i];
    const r = item.result;
    if (r === "T") {
      // Tie: mark the most recent placed cell with `tieAfter`
      if (grid[col]?.[row - 1]) grid[col][row - 1].tieAfter = true;
      else if (grid[col - 1]?.[ROWS - 1]) grid[col - 1][ROWS - 1].tieAfter = true;
      continue;
    }
    if (lastResult === null) {
      placeAt(col, row, item);
      row += 1;
    } else if (r === lastResult) {
      // Same as previous: descend in column, but at row ROWS-1 spill right (dragon tail)
      if (row < ROWS && !grid[col + 1]?.[row - 1]) {
        placeAt(col, row, item);
        row += 1;
      } else {
        // dragon tail: shift right, stay at last row
        col += 1;
        row = ROWS;
        placeAt(col, row - 1, item);
      }
    } else {
      // Flip: new column at row 0
      col = Math.max(0, getNextCol(grid, col));
      row = 0;
      placeAt(col, row, item);
      row += 1;
    }
    lastResult = r;
  }
  return { grid, cols: grid.length };
}

function getNextCol(grid, currentCol) {
  // Just move one column right from currentCol. Real baccarat has subtle "follow" rules
  // but for a streak overview a simple rightward shift is plenty readable.
  return (grid[currentCol] && grid[currentCol].length > 0) ? currentCol + 1 : currentCol;
}
